import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Radar, GitCompareArrows, Target, ArrowRight, X, Check } from "lucide-react";
import { Reveal, SectionLabel } from "./primitives";
import { CornerFrame } from "./CornerFrame";
import { Magnetic } from "./Magnetic";
import { WaitlistModal } from "./WaitlistModal";

const PANELS = [
  "AI market brief",
  "Live signal scans",
  "Market screener",
  "Live chart + AI read",
  "Confluence meter",
  "Order-book heatmap",
  "Liquidation zones",
  "Key levels & bias",
];

const PATH = [
  {
    n: "01",
    icon: Radar,
    title: "Detect",
    body: "Signals, market-moving news and live pattern detection surface what's actually moving across 800+ crypto assets — you don't hunt for the setup, it finds you.",
    tags: ["Signals", "News", "Patterns"],
  },
  {
    n: "02",
    icon: GitCompareArrows,
    title: "Cross-check",
    body: "Whale flow, liquidation clusters, funding, options gamma and order flow sit right beside the read — so you confirm or kill an idea before it costs you.",
    tags: ["Whales", "Liquidations", "Gamma", "Flow"],
  },
  {
    n: "03",
    icon: Target,
    title: "Decide",
    body: "You act on a complete picture — structure, levels and risk — instead of a lonely chart. The terminal builds the discipline; you keep the edge.",
    tags: ["Levels", "Structure", "Risk"],
  },
];

const SCATTERED = [
  ["01", "TradingView chart"],
  ["02", "A separate news feed"],
  ["03", "A whale-alert bot"],
  ["04", "A screener site"],
  ["05", "A liquidation tracker"],
  ["06", "A funding-rate page"],
];

export const TerminalWorkspace = () => {
  const [open, setOpen] = useState(false);

  return (
    <section
      id="terminal-workspace"
      data-testid="terminal-workspace"
      className="relative bg-[#070707] border-y border-white/[0.06] overflow-hidden"
    >
      <div className="pointer-events-none absolute top-0 left-1/4 h-72 w-[700px] bg-white/[0.03] blur-[130px]" />
      <div className="pointer-events-none absolute inset-0 tech-grid opacity-40" />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-28">
        {/* header */}
        <div className="max-w-3xl">
          <Reveal><SectionLabel index="FLOW">One workspace</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-display font-semibold tracking-[-0.03em] text-3xl sm:text-4xl lg:text-5xl leading-[0.98]">
              Stop tab-hopping.
              <span className="block text-[#BACD1C]">Read the whole market in one place.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-sm sm:text-base text-white/55 leading-relaxed">
              Every serious read lives across a dozen tabs — chart here, news there, whale bot, screener,
              liquidations, funding. TradrQuant keeps the signal, the context and the decision on
              <span className="text-white/85"> one screen</span>, so you validate a move
              <span className="text-[#7D8390]"> before you ever commit to it.</span>
            </p>
          </Reveal>
        </div>

        {/* terminal preview — the real workspace */}
        <Reveal delay={0.12}>
          <div className="mt-12 relative">
            <div className="pointer-events-none absolute -inset-6 bg-white/[0.03] blur-3xl" />
            <div className="relative border border-white/[0.10] bg-black overflow-hidden shadow-[0_0_90px_-25px_rgba(186,205,28,0.4)]">
              <CornerFrame />
              {/* window chrome */}
              <div className="flex items-center gap-2 border-b border-white/[0.10] bg-[#0c0c0c] px-3.5 py-2.5">
                <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-yellow-400/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#BACD1C]/80" />
                <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 truncate">terminal.tradrquant — one workspace</span>
                <span className="ml-auto flex items-center gap-1.5 shrink-0">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#BACD1C] opacity-60" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#BACD1C]" />
                  </span>
                  <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#B4BAC4]">live</span>
                </span>
              </div>
              <video
                src="/terminal.mp4"
                poster="/terminal-poster.jpg"
                autoPlay loop muted playsInline preload="metadata"
                onLoadedData={(e) => { e.currentTarget.play().catch(() => {}); }}
                onCanPlay={(e) => { e.currentTarget.play().catch(() => {}); }}
                data-testid="terminal-preview-video"
                className="w-full h-auto block select-none"
              />
            </div>
            {/* annotation chips */}
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/[0.06] border border-white/[0.06]">
              {PANELS.map((p, i) => (
                <div key={p} className="flex items-center gap-2 bg-[#0A0A0A] px-3 py-2.5" data-testid={`terminal-panel-${i}`}>
                  <span className="font-mono text-[9px] text-[#BACD1C]/70 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-white/50 truncate">{p}</span>
                </div>
              ))}
            </div>
            <p className="mt-3 font-mono text-[11px] text-white/30 tracking-[0.1em] leading-relaxed">
              A look inside the TradrQuant terminal — signals, screener, chart intelligence, order-book heat and liquidation flow, on one screen. In build · founder early release.
            </p>
          </div>
        </Reveal>

        {/* decision path */}
        <div className="mt-14 grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {PATH.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.06}>
              <div className="group h-full bg-[#0A0A0A] hover:bg-[#0E0E0E] p-6 sm:p-7 transition-colors duration-300" data-testid={`flow-step-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="inline-flex h-10 w-10 items-center justify-center border border-white/[0.10] bg-white/[0.03] text-[#BACD1C] group-hover:bg-[#BACD1C] group-hover:text-black transition-colors duration-300">
                    <p.icon size={17} />
                  </span>
                  <span className="font-mono text-[11px] tracking-[0.3em] text-white/25">{p.n}</span>
                </div>
                <h3 className="mt-4 font-display font-semibold tracking-[-0.03em] text-xl leading-tight">{p.title}</h3>
                <p className="mt-2.5 text-[13px] text-white/50 leading-relaxed">{p.body}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {p.tags.map((t) => (
                    <span key={t} className="font-mono text-[9px] uppercase tracking-[0.15em] text-white/45 border border-white/10 px-2 py-0.5">{t}</span>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* without vs with */}
        <div className="mt-6 grid lg:grid-cols-2 gap-6">
          {/* without */}
          <Reveal>
            <div className="h-full border border-white/10 bg-[#0A0A0A] p-6 sm:p-8" data-testid="workspace-without">
              <div className="flex items-center gap-2.5">
                <span className="inline-flex h-6 w-6 items-center justify-center border border-red-500/40 text-red-400/80"><X size={13} /></span>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Without TradrQuant · disconnected</span>
              </div>
              <h3 className="mt-4 font-display font-semibold tracking-[-0.03em] text-xl sm:text-2xl leading-tight text-white/85">Six tabs. Stale context.</h3>
              <div className="mt-5 grid grid-cols-2 gap-px bg-white/[0.06] border border-white/[0.06]">
                {SCATTERED.map(([n, t]) => (
                  <div key={n} className="flex items-center gap-2.5 bg-[#0B0B0B] px-3.5 py-3">
                    <span className="font-mono text-[10px] text-white/25">{n}</span>
                    <span className="font-mono text-[12px] text-white/50">{t}</span>
                  </div>
                ))}
              </div>
              <p className="mt-5 font-mono text-[12px] text-white/40 leading-relaxed">
                Every switch is another window to reconcile — and another chance to lose the thread while price moves on.
              </p>
            </div>
          </Reveal>

          {/* with */}
          <Reveal delay={0.08}>
            <div className="relative h-full border border-white/[0.10] bg-[#0E0E0E] p-6 sm:p-8 overflow-hidden" data-testid="workspace-with">
              <div className="absolute inset-0 lime-glow opacity-20" />
              <div className="relative">
                <div className="flex items-center gap-2.5">
                  <span className="inline-flex h-6 w-6 items-center justify-center border border-[#BACD1C]/50 text-[#BACD1C]"><Check size={13} /></span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#7D8390]">Inside TradrQuant · connected</span>
                </div>
                <h3 className="mt-4 font-display font-semibold tracking-[-0.03em] text-xl sm:text-2xl leading-tight">Signal → context → decision, one screen.</h3>
                <div className="mt-5 space-y-2.5">
                  {[
                    ["Source", "Signals, news & patterns surface the move"],
                    ["Reaction", "Price, liquidity & liquidations show how it landed"],
                    ["Positioning", "Whale flow, funding & gamma validate the participation"],
                    ["Decision", "Structure, levels & risk — you act with a full read"],
                  ].map(([k, v], i) => (
                    <div key={k} className="flex items-start gap-3 border-l-2 border-white/[0.10] pl-3.5">
                      <span className="font-mono text-[10px] text-[#B4BAC4] mt-0.5 w-24 shrink-0 uppercase tracking-[0.12em]">{k}</span>
                      <span className="font-mono text-[12px] text-white/60 leading-relaxed">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* closing band */}
        <Reveal delay={0.1}>
          <div className="mt-6 border border-white/10 bg-[#0A0A0A] p-8 sm:p-10 relative overflow-hidden">
            <div className="pointer-events-none absolute inset-0 tech-grid opacity-30" />
            <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
              <p className="font-display font-semibold tracking-[-0.03em] text-2xl sm:text-3xl lg:text-4xl leading-tight max-w-2xl">
                Built to make you a <span className="text-[#7D8390]">better trader</span> — not just a faster one.
              </p>
              <div className="flex flex-wrap gap-3 shrink-0">
                <Link to="/terminal" data-testid="workspace-explore-terminal"
                  className="group inline-flex items-center gap-2.5 border border-white/15 text-white/80 font-mono text-xs uppercase tracking-[0.2em] px-6 py-4 hover:text-[#BACD1C] hover:border-[#BACD1C]/40 transition-colors duration-300">
                  Explore the terminal
                  <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform duration-300" />
                </Link>
                <Magnetic strength={0.3}>
                  <button type="button" onClick={() => setOpen(true)} data-testid="workspace-waitlist-button"
                    className="group inline-flex items-center gap-2.5 bg-[#BACD1C] text-black font-mono text-xs uppercase tracking-[0.2em] px-6 py-4 hover:bg-white active:scale-[0.98] transition-[background-color,transform] duration-300">
                    Create free account
                    <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform duration-300" />
                  </button>
                </Magnetic>
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      <AnimatePresence>{open && <WaitlistModal open={open} onClose={() => setOpen(false)} />}</AnimatePresence>
    </section>
  );
};
